import React, { useState, useEffect } from 'react'

interface StarRatingProps {
	rating: number
	className?: string
	clickable: boolean
	onRatingChange?: (newRating: number) => void
	size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'xs'
	filledColor?: string
}

export const StarRating: React.FC<StarRatingProps> = ({
	rating,
	className,
	clickable,
	onRatingChange,
	size = 'md',
	filledColor = 'text-secondary-500',
}) => {
	const [currentRating, setCurrentRating] = useState(rating)

	useEffect(() => {
		setCurrentRating(rating)
	}, [rating])

	const handleClick = (index: number) => {
		if (!clickable) return

		const newRating = index + 1
		setCurrentRating(newRating)
		if (onRatingChange) {
			onRatingChange(newRating)
		}
	}

	const safeRating = Math.min(currentRating, 5)
	const fullStars = Math.floor(safeRating)
	const halfStar = safeRating % 1 >= 0.5
	const emptyStars = 5 - fullStars - (halfStar ? 1 : 0)

	return (
		<span className={`flex w-fit items-center ${className}`}>
			{Array.from({ length: fullStars }).map((_, index) => (
				<span
					key={`full-${index}`}
					className={`text-${size} ${filledColor} ${clickable ? 'cursor-pointer' : ''}`}
					onClick={() => handleClick(index)}
					role="img"
					aria-label={`Star ${index + 1} ${clickable ? 'clickable' : 'not-clickable'}`}
				>
					&#9733;
				</span>
			))}
			{halfStar && (
				<span
					key="half"
					className={`relative text-${size} text-gray-300 ${clickable ? 'cursor-pointer' : ''}`}
					onClick={() => handleClick(fullStars)}
					role="img"
					aria-label={`Half Star ${fullStars + 1} ${clickable ? 'clickable' : 'not-clickable'}`}
				>
					<span
						className={`absolute inset-0 ${filledColor}`}
						style={{ width: '50%', overflow: 'hidden' }}
					>
						&#9733;
					</span>
					&#9734;
				</span>
			)}
			{Array.from({ length: emptyStars }).map((_, index) => (
				<span
					key={`empty-${index}`}
					className={`text-${size} text-gray-300 ${clickable ? 'cursor-pointer' : ''}`}
					onClick={() => handleClick(fullStars + index)}
					role="img"
					aria-label={`Empty Star ${fullStars + index + 1} ${clickable ? 'clickable' : 'not-clickable'}`}
				>
					&#9734;
				</span>
			))}
		</span>
	)
}
